import { ButtonVariant } from '@components/buttons/button'
import { type AttributeInput } from '@data/shopify/storefront/types'
import { LanguageContext } from '@lib/language-context'
import { preorderShopifyAttribute } from '@lib/shopify/cart'
import { StringsContext } from '@lib/strings-context'
import { useContext, useEffect, useMemo, useState } from 'react'
import Countdown from 'react-countdown'
import ProductAdd from './product-add'

interface ProductPreorderCountdownProps {
  preorderCountdownTarget: string
  preorderDeliveryDate: string
  disabled?: boolean
  onClick: (customAttributes?: AttributeInput[]) => Promise<void>
}

const ProductPreorderCountdown = ({
  preorderCountdownTarget,
  preorderDeliveryDate,
  disabled,
  onClick,
}: ProductPreorderCountdownProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)

  const [isLoaded, setIsLoaded] = useState(false)

  // Load component only client side
  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(true)
    }
  }, [isLoaded])

  const preorderDescription = useMemo(() => {
    const dateFormat = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    const deliveryDate = new Date(preorderDeliveryDate)

    return strings.productPreorderDescription?.replace(
      /{delivery_date}/gi,
      dateFormat.format(deliveryDate),
    )
  }, [locale, preorderDeliveryDate, strings])

  const preorderAttributeValue = useMemo(() => {
    const dateFormat = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    const deliveryDate = new Date(preorderDeliveryDate)

    return strings.cartPreorderAttributeValue?.replace(
      /{delivery_date}/gi,
      dateFormat.format(deliveryDate),
    )
  }, [locale, preorderDeliveryDate, strings])

  if (!isLoaded) {
    return null
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <p className="uppercase">{strings.productPreorderCountdownHeading}</p>

        <Countdown
          date={preorderCountdownTarget}
          renderer={({ formatted }) => (
            <p className="font-bold text-lg uppercase">
              {strings.productPreorderCountdownFormat
                .replace(/{days}/gi, formatted.days)
                .replace(/{hours}/gi, formatted.hours)
                .replace(/{minutes}/gi, formatted.minutes)
                .replace(/{seconds}/gi, formatted.seconds)}
            </p>
          )}
          onComplete={() => {
            // Reload the page when pre-order countdown reaches 0
            window.location.reload()
          }}
        />

        <p>{preorderDescription}</p>
      </div>

      <ProductAdd
        variant={ButtonVariant.FILLED}
        onClick={() =>
          onClick([
            {
              key: preorderShopifyAttribute,
              value: 'yes',
            },
            {
              key: strings.cartPreorderAttributeKey,
              value: preorderAttributeValue,
            },
          ])
        }
        disabled={disabled}
        className="w-full"
      >
        {strings.buttonAddToCartPreorder}
      </ProductAdd>
    </div>
  )
}

export default ProductPreorderCountdown
